import { noticeLabelKeys } from '@lib/spproject/inspectionStatus';
import { type AppSettingDefinition, type AppSettingValue, type AppSettingValues } from '@store/api/appSettingsEndpoints';

/**
 * Labels and resolution for the SuPAR app's settings. The catalogue itself comes from the
 * backend (`GET /v1/app-settings/schema`); this side only names its keys and works out what a
 * team or the organization shows.
 *
 * A setting's label is derived from the last segment of its key, and a section's from the
 * middle one, so a new setting in the catalogue renders with a key the CMS can be given
 * rather than needing an entry here. Where the words already exist in the CMS they are reused.
 */

type Translate = (key: string, values?: Array<string | number> | Record<string, string | number>) => string;

const pascal = (segment: string) => segment.charAt(0).toUpperCase() + segment.slice(1);

const segmentsOf = (key: string) => key.split('.');

const labelOverrides: Record<string, string> = {
  'general.language': 'Settings_Language',
  'report.poiFields.order': 'Common_Order',
  'report.poiFields.title': 'Common_Title',
  'report.poiFields.status': 'Common_Status',
  'report.poiFields.group': 'Customization_Group',
  'report.poiFields.comment': 'Common_Comment',
  'report.poiFields.attachedPart': 'Report_Attached_Part',
  'report.poiFields.dateCreated': 'Common_DateCreated',
  'report.poiFields.dateModified': 'Common_DateModified',
  'report.poiStatuses.notChecked': noticeLabelKeys.NotChecked,
  'report.poiStatuses.warning': noticeLabelKeys.Warning,
  'report.poiStatuses.error': noticeLabelKeys.Error,
  'report.poiStatuses.ok': noticeLabelKeys.Ok,
  'report.poiTypes.comment': 'Common_Notes',
  'report.poiTypes.checkpoint': 'Common_Checkpoints',
};

const optionOverrides: Record<string, string> = {
  Order: 'Common_Order',
  Title: 'Common_Title',
};

export const groupLabelKey = (groupId: string) => `AppSettings_Group_${pascal(groupId)}`;

export const sectionLabelKey = (section: string) => `AppSettings_Section_${pascal(section)}`;

export const settingLabelKey = (key: string) => {
  const segments = segmentsOf(key);
  return labelOverrides[key] ?? `AppSettings_Field_${pascal(segments[segments.length - 1])}`;
};

export const optionLabelKey = (option: string) => optionOverrides[option] ?? `AppSettings_Option_${option}`;

/** `graphics.curve.lineWidth` sits in the `curve` section; `graphics.groundShadow` in none. */
export const sectionOf = (key: string): string | null => {
  const segments = segmentsOf(key);
  return segments.length > 2 ? segments[1] : null;
};

/** A group's settings in catalogue order, in runs that share a section (or share having none). */
export const sectionsOf = (settings: AppSettingDefinition[]) => settings.reduce<
  { section: string | null; settings: AppSettingDefinition[] }[]
>((runs, setting) => {
  const section = sectionOf(setting.key);
  const last = runs[runs.length - 1];

  if (last && last.section === section) last.settings.push(setting);
  else runs.push({ section, settings: [setting] });

  return runs;
}, []);

/**
 * Where a shown value comes from: set in the settings being edited (a team's, or the
 * organization's), set by the organization beneath a team, or the app's own default.
 */
export type ValueSource = 'own' | 'organization' | 'default';

export interface ResolvedSetting {
  value: AppSettingValue;
  source: ValueSource;
  /** What the setting falls back to without its own value, and what reverting returns to. */
  fallback: AppSettingValue;
  fallbackSource: Exclude<ValueSource, 'own'>;
}

/**
 * What the settings being edited show for a setting: their own value if they set one, else
 * what is beneath. Beneath a team that is the organization's value where it set one; beneath
 * the organization (`beneathValues` empty) and everywhere else, the app's default.
 */
export const resolveSetting = (
  definition: AppSettingDefinition, ownValues: AppSettingValues, beneathValues: AppSettingValues,
): ResolvedSetting => {
  const own = ownValues[definition.key];
  const beneath = beneathValues[definition.key];

  const fallback = beneath ?? definition.default;
  const fallbackSource = beneath === undefined ? 'default' : 'organization';

  return own !== undefined
    ? { value: own, source: 'own', fallback, fallbackSource }
    : { value: fallback, source: fallbackSource, fallback, fallbackSource };
};

/**
 * Changes not saved yet, by key: a value to set, or `null` to take the setting off. A
 * difference over the saved settings rather than a copy of them, so what somebody else saves
 * meanwhile under another key is neither shown stale nor written back.
 */
export type AppSettingsDraft = Record<string, AppSettingValue | null>;

/** The settings as saving the draft leaves them: what the page shows while it is being edited. */
export const applyDraft = (ownValues: AppSettingValues, draft: AppSettingsDraft): AppSettingValues => {
  const values = { ...ownValues };

  for (const [key, value] of Object.entries(draft)) {
    if (value === null) delete values[key];
    else values[key] = value;
  }

  return values;
};

/**
 * The draft after a choice: `value` sets the setting, `null` takes it off. A choice that comes
 * out at what is saved — the saved value chosen again, or a setting taken off that was never
 * set — leaves the draft, so changing something and changing it back is no change.
 */
export const stageValue = (
  draft: AppSettingsDraft, ownValues: AppSettingValues, key: string, value: AppSettingValue | null,
): AppSettingsDraft => {
  const { [key]: _staged, ...rest } = draft;

  return value === (ownValues[key] ?? null) ? rest : { ...rest, [key]: value };
};

/** The draft after "reset all": everything shown as set is taken off, what was only staged with it. */
export const stageReset = (draft: AppSettingsDraft, ownValues: AppSettingValues): AppSettingsDraft =>
  Object.keys(applyDraft(ownValues, draft)).reduce((next, key) => stageValue(next, ownValues, key, null), draft);

/**
 * What of a draft still differs from the saved settings. A draft outlives the read it was made
 * on, and the same value may have been saved from elsewhere since.
 */
export const pendingOf = (draft: AppSettingsDraft, ownValues: AppSettingValues): AppSettingsDraft =>
  Object.fromEntries(Object.entries(draft).filter(([key, value]) => value !== (ownValues[key] ?? null)));

export const languageName = (tag: string, displayLocale: string): string => {
  try {
    return new Intl.DisplayNames([displayLocale], { type: 'language' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
};

/** A value in words, for the places a control cannot show it — tooltips and hints. */
export const formatSettingValue = (
  definition: AppSettingDefinition, value: AppSettingValue, t: Translate, displayLocale: string,
): string => {
  switch (definition.type) {
    case 'boolean':
      return t(value ? 'AppSettings_On' : 'AppSettings_Off');
    case 'choice':
      return t(optionLabelKey(String(value)));
    case 'color':
      return String(value).slice(0, 7).toUpperCase();
    case 'lineWidth':
      return String(value);
    case 'range':
      return definition.format === 'percent' ? `${Math.round(Number(value) * 100)}%` : `×${Number(value).toFixed(2)}`;
    case 'language':
      return value === '' ? t('AppSettings_DeviceLanguage') : languageName(String(value), displayLocale);
  }
};

/** The colour a line-width preview is drawn in: its section's line colour, when it has one. */
export const lineColorKeyFor = (key: string): string => `${segmentsOf(key).slice(0, -1).join('.')}.lineColor`;
